import React, { useRef } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { SortArrowsIcon } from "../MyAppsTable/icons";
import ColumnResizer from "./ColumnResizer";
import useColumnDrag from "./useColumnDrag";
import useColumnLayout from "./useColumnLayout";
import DataCards from "./DataCards";
import useIsPhone from "../../../hooks/useIsPhone";
import { DataTableColumn, DataTableProps } from "./types";

/**
 * The table of the Apple Ads screens and the admin panel, ported from the
 * admin's DataTable: the header sticks to the top of the scroll area, the
 * fixed column to its left edge, hairline rows with a soft blue hover, and
 * the columns are resized by the handle on a header's right edge and
 * reordered by dragging the header (kept in localStorage under `id`).
 *
 * Unlike the admin's, the table is fluid: the given widths are proportions,
 * and the columns stretch together to the width of the card. Below the sum
 * of the widths the table scrolls sideways.
 *
 * Pure: the container sorts the rows (`sort` + `onSortChange`) and loads
 * more of them (`footer` holds its sentinel).
 *
 * On a phone the rows are cards (DataCards), as in the admin panel; each
 * column's `mobile` says where it goes on the card.
 */
export function DataTable<T>({
  id,
  columns,
  rows,
  rowKey,
  sort = null,
  onSortChange,
  onRowClick,
  emptyText = "No data",
  footer,
  cardOrder,
  className,
}: DataTableProps<T>) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const isPhone = useIsPhone();
  const layout = useColumnLayout(
    id,
    columns.map((column) => column.key),
  );
  const { dragProps, dragKey, overKey } = useColumnDrag(layout.reorder);

  const byKey = new Map(columns.map((column) => [column.key, column]));
  // Fixed columns stay first whatever order was saved.
  const ordered = layout.order
    .map((key) => byKey.get(key) as DataTableColumn<T>)
    .sort((a, b) => Number(Boolean(b.fixed)) - Number(Boolean(a.fixed)));
  const widthOf = (column: DataTableColumn<T>) =>
    (!column.locked && layout.widths[column.key]) || column.width;
  const tableWidth = ordered.reduce((sum, column) => sum + widthOf(column), 0);

  // The handle reports where the edge should land on screen. While the table
  // is stretched every column is drawn wider by the same factor, so the width
  // kept is the one that, stretched, comes out where the guide was.
  const resize = (column: DataTableColumn<T>, drawn: number | null) => {
    if (drawn == null) {
      layout.resize(column.key, null);
      return;
    }
    const available = scrollerRef.current?.clientWidth ?? 0;
    const others = tableWidth - widthOf(column);
    const stretched = others > 0 && drawn < available - others;
    layout.resize(
      column.key,
      stretched ? Math.round((drawn * others) / (available - drawn)) : drawn,
    );
  };

  const changeSort = (key: string) =>
    onSortChange?.(
      sort?.key === key
        ? { key, direction: sort.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );

  const cellClass = (column: DataTableColumn<T>) =>
    cs(column.className, {
      [styles.center]: column.align === "center",
      [styles.right]: column.align === "right",
      [styles.fixed]: column.fixed,
    });

  const renderTitle = (column: DataTableColumn<T>) => {
    if (!column.title) return null;
    if (!column.sortable) {
      return <span className={styles.headerText}>{column.title}</span>;
    }
    const direction = sort?.key === column.key ? sort.direction : undefined;
    return (
      <button
        type="button"
        className={styles.headerLabel}
        onClick={() => changeSort(column.key)}
      >
        <span className={styles.headerText}>{column.title}</span>
        <SortArrowsIcon
          className={styles.sortIcon}
          upClassName={direction === "asc" ? styles.sortActive : undefined}
          downClassName={direction === "desc" ? styles.sortActive : undefined}
        />
      </button>
    );
  };

  const renderHeader = (column: DataTableColumn<T>) =>
    column.headerStart ? (
      <span className={styles.headerGroup}>
        {column.headerStart}
        {renderTitle(column)}
      </span>
    ) : (
      renderTitle(column)
    );

  if (isPhone)
    return (
      <DataCards
        columns={ordered}
        rows={rows}
        rowKey={rowKey}
        sort={sort}
        onSortChange={onSortChange}
        onRowClick={onRowClick}
        emptyText={emptyText}
        order={cardOrder}
        footer={footer}
        className={className}
      />
    );

  return (
    <div ref={scrollerRef} className={cs(styles.scroller, className)}>
      <table className={styles.table} style={{ minWidth: tableWidth }}>
        <colgroup>
          {ordered.map((column) => (
            <col key={column.key} style={{ width: widthOf(column) }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {ordered.map((column) => {
              const movable = !column.fixed && !column.locked;
              const direction =
                sort?.key === column.key ? sort.direction : undefined;
              return (
                <th
                  key={column.key}
                  data-col={column.key}
                  className={cs(styles.headerCell, cellClass(column), {
                    [styles.draggable]: movable,
                    [styles.dragging]: dragKey === column.key,
                    [styles.dropTarget]: overKey === column.key,
                  })}
                  aria-sort={
                    direction
                      ? direction === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                  {...(movable ? dragProps(column.key) : {})}
                >
                  {renderHeader(column)}
                  {!column.locked && (
                    <ColumnResizer
                      boundsRef={scrollerRef}
                      label={column.title || column.key}
                      onResize={(width) => resize(column, width)}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={styles.empty} colSpan={ordered.length}>
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                className={cs({ [styles.rowClickable]: Boolean(onRowClick) })}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {ordered.map((column) => {
                  const content = column.render(row);
                  return (
                    <td
                      key={column.key}
                      data-col={column.key}
                      className={cs(styles.cell, cellClass(column))}
                      // Cut text is still readable on hover.
                      title={
                        typeof content === "string" ||
                        typeof content === "number"
                          ? String(content)
                          : undefined
                      }
                      onClick={
                        column.stopRowClick
                          ? (event) => event.stopPropagation()
                          : undefined
                      }
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {footer}
    </div>
  );
}

export default DataTable;
